import type { ScoreBreakdown } from '../scoring/Scoring';
import { moodLabel } from './pandaFace';

export interface MenuCallbacks {
  onStartLevel: (index: number) => void;
  onResume: () => void;
  onRestart: () => void;
  onQuit: () => void;
}

// Title / pause / results screens (§7, §9 finish) plus the always-on pause
// button. All start/restart actions flow through here so audio + fullscreen can
// be kicked off on the first user gesture (§8).
export class Menus {
  private root: HTMLElement;
  readonly pauseButton: HTMLButtonElement;
  private title: HTMLElement;
  private pause: HTMLElement;
  private results: HTMLElement;
  private breakdownEl: HTMLElement;

  constructor(parent: HTMLElement, private levelNames: string[], private cb: MenuCallbacks) {
    this.root = document.createElement('div');
    this.root.style.position = 'absolute';
    this.root.style.inset = '0';
    this.root.style.pointerEvents = 'none';
    parent.appendChild(this.root);

    this.pauseButton = document.createElement('button');
    this.pauseButton.className = 'pause-btn hidden';
    this.pauseButton.textContent = '❚❚';
    this.pauseButton.setAttribute('aria-label', 'Pause');
    this.root.appendChild(this.pauseButton);

    this.title = this.makeTitle();
    this.pause = this.makePause();
    this.results = this.makeResults();
    this.root.append(this.title, this.pause, this.results);
    this.breakdownEl = this.results.querySelector('#result-breakdown') as HTMLElement;
  }

  private makeTitle(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'menu';
    el.innerHTML = `
      <div class="menu-inner">
        <div class="panda-viewer-mount" id="title-panda"></div>
        <h1>Panda Roll</h1>
        <p class="hint">Steer through the curves and jump over trouble — pick a course.
        (drag the panda!)<br/>
        Keyboard: A/D · Space · Esc &nbsp;·&nbsp; Touch: ◀ ▶ steer · tap to jump</p>
        <div class="level-list" id="level-list"></div>
      </div>
    `;
    const list = el.querySelector('#level-list') as HTMLElement;
    this.levelNames.forEach((name, i) => {
      const b = document.createElement('button');
      b.className = i === 0 ? 'btn level-btn' : 'btn secondary level-btn';
      b.innerHTML = `<span class="level-num">${i + 1}</span><span>${name}</span>`;
      b.addEventListener('click', () => this.cb.onStartLevel(i));
      list.appendChild(b);
    });
    return el;
  }

  private makePause(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'menu hidden';
    el.innerHTML = `
      <div class="menu-inner">
        <h2>Paused</h2>
        <button class="btn" id="pause-resume">Resume</button>
        <button class="btn secondary" id="pause-restart">Restart</button>
        <button class="btn secondary" id="pause-quit">Quit to Menu</button>
      </div>
    `;
    (el.querySelector('#pause-resume') as HTMLElement).addEventListener('click', () => this.cb.onResume());
    (el.querySelector('#pause-restart') as HTMLElement).addEventListener('click', () => this.cb.onRestart());
    (el.querySelector('#pause-quit') as HTMLElement).addEventListener('click', () => this.cb.onQuit());
    return el;
  }

  private makeResults(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'menu hidden';
    el.innerHTML = `
      <div class="menu-inner">
        <div class="panda-viewer-mount result-panda" id="result-panda"></div>
        <h2 id="result-heading">Finish!</h2>
        <div class="breakdown" id="result-breakdown"></div>
        <button class="btn" id="result-retry">Retry</button>
        <button class="btn secondary" id="result-levels">Choose Level</button>
      </div>
    `;
    (el.querySelector('#result-retry') as HTMLElement).addEventListener('click', () => this.cb.onRestart());
    (el.querySelector('#result-levels') as HTMLElement).addEventListener('click', () => this.cb.onQuit());
    return el;
  }

  showTitle(): void {
    this.title.classList.remove('hidden');
    this.pause.classList.add('hidden');
    this.results.classList.add('hidden');
    this.pauseButton.classList.add('hidden');
  }

  showPlaying(): void {
    this.title.classList.add('hidden');
    this.pause.classList.add('hidden');
    this.results.classList.add('hidden');
    this.pauseButton.classList.remove('hidden');
  }

  showPause(): void {
    this.pause.classList.remove('hidden');
  }

  hidePause(): void {
    this.pause.classList.add('hidden');
  }

  showResults(b: ScoreBreakdown, time: number, mood: number): void {
    this.pauseButton.classList.add('hidden');
    (this.results.querySelector('#result-heading') as HTMLElement).textContent = `Finish! ${moodLabel(mood)}`;
    const rows: [string, number][] = [
      ['Collectibles', b.collectibles],
      ['Combo bonus', b.combo],
      [`Time (${time.toFixed(1)}s)`, b.time],
      ['Air bonus', b.air],
    ];
    this.breakdownEl.innerHTML =
      rows
        .map(
          ([label, val]) =>
            `<div class="row"><span>${label}</span><span>${val.toLocaleString()}</span></div>`,
        )
        .join('') +
      `<div class="row"><span>Best chain</span><span>${b.bestStreak}</span></div>` +
      `<div class="row total"><span>Total</span><span id="result-total">0</span></div>`;
    this.results.classList.remove('hidden');

    // Count-up animation (§9 big score count-up).
    const totalEl = this.breakdownEl.querySelector('#result-total') as HTMLElement;
    const start = performance.now();
    const dur = 1100;
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      totalEl.textContent = Math.round(b.total * eased).toLocaleString();
      if (k < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}
